import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap,
  ShieldCheck,
  Award,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";

// 4 hero credentials shown as premium badges (always visible)
const heroCredentials = [
  {
    short: "NASM",
    title: "National Academy of Sports Medicine",
    note: "Certified",
  },
  {
    short: "ACSM",
    title: "American College of Sports Medicine",
    note: "Certified",
  },
  {
    short: "NSCA CSCS",
    title: "Certified Strength and Conditioning Specialist",
    note: "NSCA",
  },
  {
    short: "REPs",
    title: "UAE REPs Registered Professional",
    note: "Registered",
  },
];

const additionalCredentials = [
  { icon: GraduationCap, label: "Bachelor's Degree" },
  { icon: ShieldCheck, label: "Certified Personal Trainer" },
  { icon: Award, label: "ACE, American Council on Exercise" },
];

// Categorized specializations
const categories = [
  {
    key: "corrective",
    label: "Corrective Exercise",
    items: [
      "Corrective Exercise Specialist",
      "Human Movement Specialist",
      "Posture & Movement Analysis",
      "Client Assessment & Movement Screening",
      "Pain Management Through Exercise",
      "Injury Prevention Training",
    ],
  },
  {
    key: "mobility",
    label: "Mobility & Recovery",
    items: [
      "Mobility & Flexibility Coaching",
      "Rehabilitation & Recovery Training",
      "Core Stability & Balance Training",
    ],
  },
  {
    key: "strength",
    label: "Strength & Performance",
    items: [
      "Strength & Conditioning",
      "Functional Training Specialist",
      "Sports Performance Enhancement",
      "Cardiovascular Conditioning",
      "Fat Loss & Muscle Building Programs",
    ],
  },
  {
    key: "lifestyle",
    label: "Lifestyle & Specialized",
    items: [
      "Weight Management Specialist",
      "Special Population Training",
      "Senior Fitness Training",
      "Pre & Post Natal Training",
      "Lifestyle & Wellness Coaching",
      "Personalized Exercise Programming",
    ],
  },
];

export const Qualifications = () => {
  const [activeCat, setActiveCat] = useState(categories[0].key);
  const [showAll, setShowAll] = useState(false);

  const active = categories.find((c) => c.key === activeCat) || categories[0];

  return (
    <section
      id="qualifications"
      data-testid="qualifications-section"
      className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="aurora" style={{ background: "#0055FF", width: 600, height: 600, top: -150, left: -200, opacity: 0.18 }}></div>
      <div className="aurora" style={{ background: "#00E5FF", width: 500, height: 500, bottom: -200, right: -150, opacity: 0.14 }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl mb-12 sm:mb-14">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">
            Professional Background
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
            Qualifications &amp; <span className="text-gradient-blue">Certifications.</span>
          </h2>
          <p className="mt-4 sm:mt-5 text-zinc-300/85 text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl">
            Advanced corrective exercise, rehabilitation, and performance-based coaching expertise.
          </p>
        </div>

        {/* Hero credential badges */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-14">
          {heroCredentials.map((c, i) => (
            <motion.div
              key={c.short}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`hero-cred-${i}`}
              className="group relative rounded-2xl p-5 sm:p-6 glass glass-hover overflow-hidden"
            >
              <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#00E5FF]/0 group-hover:bg-[#00E5FF]/15 blur-2xl transition-colors duration-500"></div>
              <div className="flex items-center justify-between gap-2">
                <div className="font-display text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight bg-gradient-to-br from-white to-[#00E5FF] bg-clip-text text-transparent leading-none">
                  {c.short}
                </div>
                <Award className="w-4 h-4 text-[#00E5FF]/70 shrink-0" />
              </div>
              <div className="mt-3 sm:mt-4 text-[13px] sm:text-sm text-zinc-200 leading-snug font-medium">
                {c.title}
              </div>
              <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                {c.note}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional credentials – compact row */}
        <div className="flex flex-wrap gap-2.5 sm:gap-3 mb-12 sm:mb-16">
          {additionalCredentials.map((c) => (
            <div
              key={c.label}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.03] border border-white/10 text-zinc-200 text-xs sm:text-sm"
            >
              <c.icon className="w-3.5 h-3.5 text-[#00E5FF]" />
              {c.label}
            </div>
          ))}
        </div>

        {/* Category Tabs */}
        <div className="mb-7 sm:mb-9">
          <div className="flex items-center gap-2 mb-5">
            <span className="h-px w-10 sm:w-14 bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-[#00E5FF]"></span>
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-400 font-medium">
              Specializations
            </span>
          </div>

          {/* Scrollable pill tabs */}
          <div className="-mx-5 sm:mx-0 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 px-5 sm:px-0 sm:flex-wrap">
              {categories.map((c) => {
                const isActive = c.key === activeCat;
                return (
                  <button
                    key={c.key}
                    onClick={() => {
                      setActiveCat(c.key);
                      setShowAll(false);
                    }}
                    data-testid={`qual-tab-${c.key}`}
                    className={`shrink-0 px-4 sm:px-5 py-2.5 rounded-full text-[12px] sm:text-sm font-medium tracking-wide transition-all duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-black shadow-[0_10px_30px_-10px_rgba(0,229,255,0.6)]"
                        : "bg-white/[0.03] text-zinc-300 border border-white/10 hover:border-[#00E5FF]/30 hover:text-white"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Active category items */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4"
          >
            {active.items.map((label, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                data-testid={`qual-item-${i}`}
                className="group flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl border border-white/[0.07] bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#00E5FF]/25 hover:translate-x-1 transition-all duration-300"
              >
                <CheckCircle2 className="w-[18px] h-[18px] text-[#00E5FF] shrink-0" strokeWidth={2} />
                <span className="text-zinc-200 text-[13px] sm:text-[15px] leading-snug">
                  {label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* View all toggle */}
        <div className="mt-9 sm:mt-11 flex justify-center">
          <button
            onClick={() => setShowAll((s) => !s)}
            data-testid="qual-view-all-toggle"
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 rounded-full border border-white/15 text-white text-xs sm:text-sm font-medium tracking-wide hover:border-[#00E5FF]/40 hover:bg-white/5 transition-colors"
          >
            {showAll ? "Hide Full List" : "View All Certifications"}
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
              className="overflow-hidden"
            >
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {categories.flatMap((c) => c.items).map((label, i) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-white/[0.05] bg-white/[0.015]"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#00E5FF]/70 shrink-0" strokeWidth={2} />
                    <span className="text-zinc-300 text-[13px] sm:text-sm">{label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-12 sm:mt-14 text-center">
          <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-[0.25em]">
            Internationally recognized · Continuous education
          </p>
        </div>
      </div>
    </section>
  );
};

export default Qualifications;
